import { useState } from 'react';
import {
  ActionIcon,
  Box,
  Group,
  Loader,
  Menu,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { IconDots, IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  type PsychomotorDevelopmentItem,
  type PsychomotorDevelopmentType,
  PSYCHOMOTOR_DEVELOPMENT_TYPES as ALL_TYPES,
} from '@/types/psychomotor-development.types';
import { LabeledField } from '@/components/LabeledField';
import DeleteConfirmModal from '@/pages/ainigma/patient/images-tab/DeleteModal';
import { ListContentAction } from '../../medical-history/utils/medical-history.types';

export interface PsychomotorDevelopmentListItemProps {
  items: PsychomotorDevelopmentItem[];
  onAction: (action: ListContentAction) => void;
  isDeleting?: boolean;
  readonly?: boolean;
}

export const PsychomotorDevelopmentListItem = ({
  items,
  onAction,
  isDeleting = false,
  readonly = false,
}: PsychomotorDevelopmentListItemProps) => {
  const { t } = useTranslation(['patient-pages']);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);

  // Create a map of items by type for quick lookup
  const itemsMap = new Map(items.map((item) => [item.type, item]));

  // Format boolean value to Yes/No
  const formatValue = (value: boolean | null): string => {
    if (value === null) {
      return t('psychomotorDevelopment.notAnswered', {
        ns: 'patient-pages',
        defaultValue: 'Not answered',
      });
    }

    if (value) {
      return t('psychomotorDevelopment.yes', {
        ns: 'patient-pages',
        defaultValue: 'Yes',
      });
    }

    return t('psychomotorDevelopment.no', {
      ns: 'patient-pages',
      defaultValue: 'No',
    });
  };

  // Get type label
  const getTypeLabel = (type: PsychomotorDevelopmentType): string => {
    return t(`psychomotorDevelopment.${type}`, {
      ns: 'patient-pages',
      defaultValue: type,
    });
  };

  // Count answered items
  const answeredCount = items.filter(
    (item) => item.value !== null || item.observations
  ).length;

  const hasActions = !isDeleting && !readonly;

  const handleDeleteClick = () => {
    setDeleteModalOpened(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteModalOpened(false);
    onAction('remove');
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpened(false);
  };

  return (
    <>
      <Paper p="sm" withBorder radius="md" h="100%">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Box flex={1}>
            {/* Header */}
            <Group justify="space-between" mb="xs">
              <Text fw={600} size="sm">
                {t('psychomotorDevelopment.assessment', {
                  ns: 'patient-pages',
                  defaultValue: 'Psychomotor Development Assessment',
                })}
              </Text>
              <Text size="xs" c="dimmed">
                {answeredCount} / {ALL_TYPES.length}{' '}
                {t('psychomotorDevelopment.answered', {
                  ns: 'patient-pages',
                  defaultValue: 'answered',
                })}
              </Text>
            </Group>

            {/* Display each type */}
            <Stack gap="xs">
              {ALL_TYPES.map((type) => {
                const item = itemsMap.get(type);
                if (!item) {
                  return null;
                }

                // Only show items that have been answered or have observations
                if (item.value === null && !item.observations) {
                  return null;
                }

                return (
                  <Box key={type}>
                    <LabeledField
                      label={getTypeLabel(type)}
                      value={formatValue(item.value)}
                    />
                    {item.observations && (
                      <Box ml="md" mt="xs">
                        <Text size="xs" c="dimmed">
                          {t('psychomotorDevelopment.observations', {
                            ns: 'patient-pages',
                            defaultValue: 'Observations',
                          })}
                          : {item.observations}
                        </Text>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {/* Actions menu or loading indicator */}
          {isDeleting ? (
            <Loader size="sm" />
          ) : (
            hasActions && (
              <Menu shadow="md" width={160} position="bottom-end">
                <Menu.Target>
                  <ActionIcon variant="subtle" color="gray" size="sm">
                    <IconDots size={16} />
                  </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={() => onAction('edit')}
                  >
                    {t('edit', {
                      ns: 'common',
                      defaultValue: 'Edit',
                    })}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconTrash size={14} />}
                    onClick={handleDeleteClick}
                    color="red"
                  >
                    {t('delete', {
                      ns: 'common',
                      defaultValue: 'Delete',
                    })}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )
          )}
        </Group>
      </Paper>

      <DeleteConfirmModal
        opened={deleteModalOpened}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
        title={t('psychomotorDevelopment.confirmDelete', {
          ns: 'patient-pages',
          defaultValue: 'Confirm Delete',
        })}
        message={t('psychomotorDevelopment.confirmDeleteMessage', {
          ns: 'patient-pages',
          defaultValue:
            'Are you sure you want to delete this psychomotor development assessment? This action cannot be undone.',
        })}
      />
    </>
  );
};
